import { useEffect, useCallback } from 'react'

type Variant = 'danger' | 'warning'

interface Props {
  isOpen: boolean
  title: string
  message: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  variant?: Variant
  submitting?: boolean
  onConfirm: () => void
  onClose: () => void
}

export default function ConfirmModal({
  isOpen,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  submitting = false,
  onConfirm,
  onClose,
}: Props) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose()
    },
    [onClose, submitting]
  )

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = ''
    }
  }, [isOpen, handleKeyDown])

  if (!isOpen) return null

  const iconBg = variant === 'danger' ? 'bg-pinnacle-red/10' : 'bg-[#B87A20]/10'
  const iconColor = variant === 'danger' ? 'text-pinnacle-red' : 'text-[#B87A20]'
  const confirmBtn =
    variant === 'danger'
      ? 'bg-pinnacle-red hover:bg-pinnacle-red-dark'
      : 'bg-[#B87A20] hover:bg-[#B87A20]/90'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-[#351E1D]/50 backdrop-blur-sm"
        onClick={() => {
          if (!submitting) onClose()
        }}
      />
      <div className="relative bg-white rounded-xl shadow-elevated w-full max-w-md mx-4">
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${iconBg}`}>
              <svg className={`w-5 h-5 ${iconColor}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-text-primary font-heading">{title}</h3>
          </div>

          <div className="text-sm text-text-primary mb-6">{message}</div>

          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-text-primary hover:text-text-primary transition-colors disabled:opacity-50"
            >
              {cancelLabel}
            </button>
            <button
              onClick={onConfirm}
              disabled={submitting}
              className={`px-5 py-2 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 ${confirmBtn}`}
            >
              {submitting ? 'Working...' : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
